import { BulkActionsToolbar } from "@/components/admin/bulk-actions-toolbar";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";
import jsonExport from "jsonexport/dist";
import {
  downloadCSV,
  InfiniteListBase,
  ListBase,
  useGetIdentity,
  useListContext,
  type Exporter,
} from "ra-core";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import { LeadsSearchBar } from "../misc/ResponsiveFilters";
import type { Company, Contact, Sale, Tag } from "../types";
import { ContactEmpty } from "./ContactEmpty";
import { ContactImportButton } from "./ContactImportButton";
import {
  ContactListContent,
  ContactListContentMobile,
} from "./ContactListContent";
import {
  ContactListFilter,
  ContactListFilterSummary,
} from "./ContactListFilter";

export const ContactList = () => {
  const { data: identity } = useGetIdentity();

  if (!identity) return null;

  return (
    <ListBase
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
    >
      <ContactListLayoutDesktop />
    </ListBase>
  );
};

const ContactListLayoutDesktop = () => {
  const { data, isPending, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (isPending) return null;

  if (!data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div className="flex flex-row gap-0 h-full mt-0 items-stretch overflow-hidden w-full">
      <ContactListFilter />
      <div className="erp-list-container flex-1 min-w-0 px-6 flex flex-col h-full pb-4">
        {/* Top Header Action Bar */}
        <div className="flex items-center justify-between pb-4 pt-6 shrink-0 mt-2">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 ml-2">Leads</h2>
          </div>
          <TopToolbar>
            <LeadsSearchBar />
            <SortButton 
              fields={["first_name", "last_name", "last_seen"]} 
              className="bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 transition-colors h-9 rounded-lg font-semibold cursor-pointer dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50 dark:hover:bg-teal-900/40"
            />
            <ContactImportButton 
              className="bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 transition-colors h-9 rounded-lg font-semibold cursor-pointer dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50 dark:hover:bg-teal-900/40"
            />
            <ExportButton 
              exporter={exporter} 
              className="bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 transition-colors h-9 rounded-lg font-semibold cursor-pointer dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50 dark:hover:bg-teal-900/40"
            />
            <CreateButton />
          </TopToolbar>
        </div>

        {/* Professional Table Card Container */}
        <div className="flex-1 flex flex-col min-h-0 border border-border rounded-xl bg-card shadow-sm overflow-hidden mb-2">
          {/* Scrollable grid contents */}
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            <ContactListContent />
          </div>

          {/* Clean, rectangular docked footer pagination */}
          <div className="bg-muted/20 border-t border-border py-2.5 px-4 shrink-0">
            <ListPagination />
          </div>
        </div>
      </div>
      <BulkActionsToolbar />
    </div>
  );
};

export const ContactListMobile = () => {
  const { data: identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "last_seen", order: "DESC" }}
      exporter={exporter}
      queryOptions={{
        onError: () => {
          /* Disable error notification as ContactListLayoutMobile handles it */
        },
      }}
    >
      <ContactListLayoutMobile />
    </InfiniteListBase>
  );
};

const ContactListLayoutMobile = () => {
  const { isPending, data, error, filterValues } = useListContext();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!isPending && !data?.length && !hasFilters) return <ContactEmpty />;

  return (
    <div>
      <MobileHeader>
        <ContactListFilter />
      </MobileHeader>
      <MobileContent>
        <ContactListFilterSummary />
        <ContactListContentMobile />
        {/* Mobile infinity scroll */}
        {!error && (
          <div className="flex justify-center">
            <InfinitePagination />
          </div>
        )}
      </MobileContent>
    </div>
  );
};

const exporter: Exporter<Contact> = async (records, fetchRelatedRecords, dataProvider) => {
  const companies = await fetchRelatedRecords<Company>(
    records,
    "company_id",
    "companies",
  );
  const sales = await fetchRelatedRecords<Sale>(records, "sales_id", "sales");
  
  // Fetch tags using dataProvider.getMany to handle array field correctly
  const allTagIds = Array.from(
    new Set(records.flatMap((r) => r.tags || []))
  );
  let tagsMap: Record<string | number, Tag> = {};
  if (allTagIds.length > 0 && dataProvider) {
    try {
      const { data: tagRecords } = await dataProvider.getMany("tags", {
        ids: allTagIds,
      });
      tagsMap = tagRecords.reduce((acc, tag) => {
        acc[tag.id] = tag;
        return acc;
      }, {} as Record<string | number, Tag>);
    } catch (e) {
      console.error("Failed to fetch tags for export", e);
    }
  }

  const contacts = records.map((contact) => {
    const exportedContact = {
      ...contact,
      company:
        contact.company_id != null
          ? companies[contact.company_id]?.name
          : undefined,
      sales: contact.sales_id != null && sales[contact.sales_id]
        ? `${sales[contact.sales_id].first_name} ${sales[contact.sales_id].last_name
        }`
        : "",
      tags: contact.tags ? contact.tags.map((tagId) => tagsMap[tagId]?.name || "").filter(Boolean).join(", ") : "",
      email_work: contact.email_jsonb?.find((email) => email.type === "Work")
        ?.email,
      email_home: contact.email_jsonb?.find((email) => email.type === "Home")
        ?.email,
      email_other: contact.email_jsonb?.find((email) => email.type === "Other")
        ?.email,
      email_jsonb: JSON.stringify(contact.email_jsonb),
      email_fts: undefined,
      phone_work: contact.phone_jsonb?.find((phone) => phone.type === "Work")
        ?.number,
      phone_home: contact.phone_jsonb?.find((phone) => phone.type === "Home")
        ?.number,
      phone_other: contact.phone_jsonb?.find((phone) => phone.type === "Other")
        ?.number,
      phone_jsonb: JSON.stringify(contact.phone_jsonb),
      phone_fts: undefined,
    };
    delete exportedContact.email_fts;
    delete exportedContact.phone_fts;
    return exportedContact;
  });
  return jsonExport(contacts, {}, (_err: any, csv: string) => {
    downloadCSV(csv, "contacts");
  });
};
